import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Inbox } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';
import { Skeleton } from '@/shared/ui/Skeleton';

interface DataTableProps<TData> {
  readonly columns: readonly ColumnDef<TData>[];
  readonly data: readonly TData[];
  readonly isLoading?: boolean;
  readonly emptyMessage?: string;
  readonly emptyAction?: ReactNode;
  readonly className?: string;
  readonly rowsPerSkeleton?: number;
  readonly getRowId?: (row: TData, index: number) => string;
}

/**
 * Generic table powered by @tanstack/react-table. Renders a skeleton while
 * loading and an empty state when there are no rows. Sorting/filtering happens
 * server-side, so this component is intentionally headless about it.
 */
export function DataTable<TData>({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No hay registros para mostrar.',
  emptyAction,
  className,
  rowsPerSkeleton = 5,
  getRowId,
}: DataTableProps<TData>): ReactElement {
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table opts out of React Compiler memoization by design; this is the documented integration pattern.
  const table = useReactTable<TData>({
    data: data as TData[],
    columns: columns as ColumnDef<TData>[],
    getCoreRowModel: getCoreRowModel(),
    ...(getRowId !== undefined ? { getRowId } : {}),
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const columnCount = columns.length || 1;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-border bg-card shadow-soft-1',
        className,
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="border-b border-border bg-muted/40">
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    style={
                      header.getSize() !== 150
                        ? { width: header.getSize() }
                        : undefined
                    }
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: rowsPerSkeleton }).map((_, rowIndex) => (
                <tr
                  key={`skeleton-${String(rowIndex)}`}
                  className="border-b border-border last:border-b-0"
                >
                  {Array.from({ length: columnCount }).map((__, cellIndex) => (
                    <td
                      key={`skeleton-${String(rowIndex)}-${String(cellIndex)}`}
                      className="px-4 py-3"
                    >
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="px-4 py-12 text-center">
                  <div className="mx-auto flex max-w-sm flex-col items-center gap-2 text-muted-foreground">
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-muted text-muted-foreground/80">
                      <Inbox className="h-5 w-5" />
                    </span>
                    <p className="text-sm font-medium text-foreground">
                      {emptyMessage}
                    </p>
                    {emptyAction}
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-border last:border-b-0 transition-colors hover:bg-muted/30"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 align-middle">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
